import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { mediaUrl, money } from "@/lib/api";
import StarRating from "@/components/StarRating";

export const ProductCard = ({ product, index = 0 }) => {
  const soldOut = (product.stock ?? 0) <= 0;
  const img = mediaUrl((product.images || [])[0]);
  const hasVariants = (product.variants || []).length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      className="group relative"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block relative overflow-hidden bg-noir-2 aspect-[4/5]">
        {img ? (
          <img
            src={img}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 font-serif">No image</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        {soldOut && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="border border-gold/60 text-gold font-body uppercase tracking-[0.3em] text-xs px-4 py-2">
              Sold Out
            </span>
          </div>
        )}
        {product.collection && !soldOut && (
          <span className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-champagne font-body uppercase tracking-[0.15em] text-[10px] px-2.5 py-1 border border-gold/20">
            {product.collection}
          </span>
        )}
      </Link>

      <div className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <Link to={`/product/${product.id}`} className="min-w-0">
            <h3 className="font-serif text-lg text-cream leading-snug group-hover:text-gold transition-colors truncate">
              {product.name}
            </h3>
          </Link>
          <span className="font-body text-gold text-sm whitespace-nowrap mt-1">{hasVariants ? `From ${money(product.price)}` : money(product.price)}</span>
        </div>
        <p className="font-body text-sm text-white/50 mt-1 line-clamp-2">{product.short_description}</p>

        <div className="flex items-center justify-between mt-3">
          {product.review_count > 0 ? (
            <div className="flex items-center gap-1.5">
              <StarRating value={product.rating} size={13} />
              <span className="font-body text-[11px] text-white/40">({product.review_count})</span>
            </div>
          ) : <span className="font-body text-[11px] text-white/30">New</span>}

          <Link
            to={`/product/${product.id}`}
            data-testid={`add-to-cart-${product.id}`}
            className={`inline-flex items-center gap-1.5 border border-gold/50 text-gold font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:bg-gold hover:text-black transition-colors duration-300 ${soldOut ? "opacity-30 pointer-events-none" : ""}`}
          >
            <Plus className="w-3 h-3" /> {soldOut ? "Sold Out" : "Choose Options"}
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
